import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, Rectangle, ReferenceArea } from 'recharts';
import { calculateCGP, calculateCGE } from '../../utils/metrics/cgp.js';

const getDayOfYear = (date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  return Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
};

// Day-of-year from an ascent's UTC-correct year/month/day fields
const getAscentDayOfYear = (ascent) => {
  const startOfYearUTC = Date.UTC(ascent.year, 0, 1);
  const ascentUTC = Date.UTC(ascent.year, ascent.month - 1, ascent.day);
  return Math.floor((ascentUTC - startOfYearUTC) / 86400000) + 1;
};

const colors = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#4f46e5', // indigo-600
  '#c026d3'  // fuchsia-600
];

const RoutesPerYearChart = ({ data, climbingStyle, selectedStat = 'cleanAscents' }) => {
  // Get current date and year
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentDayOfYear = getDayOfYear(currentDate);

  // Calculate month boundaries and centers for tick marks
  const monthInfo = useMemo(() => {
    const boundaries = [];
    const centers = [];
    
    for (let i = 0; i < 12; i++) {
      const startDate = new Date(2000, i, 1);
      const endDate = i < 11 ? new Date(2000, i + 1, 1) : new Date(2000, 11, 31);
      const startDay = getDayOfYear(startDate);
      const endDay = getDayOfYear(endDate);
      
      boundaries.push(startDay);
      centers.push(Math.floor(startDay + (endDay - startDay) / 2));
    }

    return { boundaries, centers };
  }, []);

  const years = useMemo(() => {
    return [...new Set(data.map(ascent => ascent.year))].sort();
  }, [data]);

  // Calculate annual totals for each year
  const annualTotals = useMemo(() => {
    const totals = {};
    years.forEach(year => {
      const yearData = data.filter(ascent => ascent.year === year);

      if (selectedStat === 'cleanAscents') {
        totals[year] = yearData.filter(ascent => ascent.isClean).length;
      } else if (selectedStat === 'allAscents') {
        totals[year] = yearData.length;
      } else if (selectedStat === 'routes' || selectedStat === 'cleanRoutes') {
        const uniqueRoutes = new Set(
          yearData
            .filter(ascent => selectedStat === 'routes' || ascent.isClean)
            .map(ascent => ascent["Route ID"])
        );
        totals[year] = uniqueRoutes.size;
      } else if (selectedStat === 'climbingDays') {
        const uniqueDays = new Set(
          yearData.map(ascent => getAscentDayOfYear(ascent))
        );
        totals[year] = uniqueDays.size;
      } else if (selectedStat === 'cumulativeGrade') {
        const cleanAscents = yearData.filter(ascent => ascent.isClean);
        totals[year] = calculateCGP(cleanAscents, climbingStyle).toFixed(1);
      } else if (selectedStat === 'cumulativeEffort') {
        totals[year] = calculateCGE(yearData, climbingStyle).toFixed(1);
      }
    });
    return totals;
  }, [data, years, selectedStat, climbingStyle]);

  // Calculate month boundaries for background
  const monthBoundaries = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(2000, i, 1);
      return getDayOfYear(date);
    });
  }, []);

  const fullYearDays = useMemo(() => {
    return Array.from({ length: 365 }, (_, i) => i + 1);
  }, []);

  const emptyChartData = useMemo(() => {
    return fullYearDays.map(day => ({
      dayOfYear: day,
      ...years.reduce((acc, year) => ({ ...acc, [`year${year}`]: null }), {})
    }));
  }, [fullYearDays, years]);

  const chartData = useMemo(() => {
    const processedData = [...emptyChartData];
    const yearCounts = {};
    const yearAscents = {};
    const firstCleanAscentDay = {};
    const climbingDays = {};
    const cumulativeClimbingDays = {};
    
    years.forEach(year => {
      yearCounts[year] = { count: 0, routes: new Set() };
      yearAscents[year] = [];
      firstCleanAscentDay[year] = null;
      climbingDays[year] = new Set();
      cumulativeClimbingDays[year] = new Set();
    });

    const sortedData = [...data].sort((a, b) =>
      a["Ascent Date"].localeCompare(b["Ascent Date"])
    );

    // First pass to gather all climbing days
    sortedData.forEach(ascent => {
      const year = ascent.year;
      const dayOfYear = getAscentDayOfYear(ascent);

      yearAscents[year].push(ascent);
      climbingDays[year].add(dayOfYear);

      if (ascent.isClean && firstCleanAscentDay[year] === null) {
        firstCleanAscentDay[year] = dayOfYear;
      }
    });

    // Initialize all days to start
    processedData.forEach(dayData => {
      years.forEach(year => {
        dayData[`year${year}`] = 0;
      });
    });

    sortedData.forEach(ascent => {
      const year = ascent.year;
      const dayOfYear = getAscentDayOfYear(ascent);
      const routeId = ascent["Route ID"];

      if (selectedStat === 'cleanAscents' && !ascent.isClean) return;
      if (selectedStat === 'cleanRoutes' && !ascent.isClean) return;

      // Determine the last day to update for this year
      const lastDayToUpdate = year === currentYear ? Math.min(currentDayOfYear, 365) : 365;

      if (selectedStat === 'climbingDays') {
        if (!cumulativeClimbingDays[year].has(dayOfYear)) {
          cumulativeClimbingDays[year].add(dayOfYear);
          // Update all subsequent days with the new cumulative count
          for (let i = dayOfYear - 1; i < lastDayToUpdate; i++) {
            processedData[i][`year${year}`] = cumulativeClimbingDays[year].size;
          }
        }
      } else if (selectedStat === 'cumulativeGrade') {
        if (!ascent.isClean) return;

        const cgp = calculateCGP(yearAscents[year].filter(a =>
          getAscentDayOfYear(a) <= dayOfYear && a.isClean
        ), climbingStyle);

        for (let i = dayOfYear - 1; i < lastDayToUpdate; i++) {
          processedData[i][`year${year}`] = cgp;
        }
      } else if (selectedStat === 'cumulativeEffort') {
        const cge = calculateCGE(yearAscents[year].filter(a =>
          getAscentDayOfYear(a) <= dayOfYear
        ), climbingStyle);

        for (let i = dayOfYear - 1; i < lastDayToUpdate; i++) {
          processedData[i][`year${year}`] = cge;
        }
      } else if (selectedStat === 'routes' || selectedStat === 'cleanRoutes') {
        if (!yearCounts[year].routes.has(routeId)) {
          yearCounts[year].routes.add(routeId);
          yearCounts[year].count++;
          
          for (let i = dayOfYear - 1; i < lastDayToUpdate; i++) {
            processedData[i][`year${year}`] = yearCounts[year].count;
          }
        }
      } else {
        yearCounts[year].count++;
        for (let i = dayOfYear - 1; i < lastDayToUpdate; i++) {
          processedData[i][`year${year}`] = yearCounts[year].count;
        }
      }
    });

    // For the current year, set all days after the current day to null
    if (years.includes(currentYear)) {
      processedData.forEach(dayData => {
        if (dayData.dayOfYear > currentDayOfYear) {
          dayData[`year${currentYear}`] = null;
        }
      });
    }

    return processedData;
  }, [data, selectedStat, emptyChartData, years, climbingStyle, currentYear, currentDayOfYear]);

  const formatDayOfYear = (dayOfYear) => {
    const date = new Date(2000, 0, dayOfYear);
    return date.toLocaleDateString('en-GB', { month: 'short' });
  };

  const getYAxisLabel = () => ({
    cleanAscents: 'Cumulative Clean Ascents',
    allAscents: 'Cumulative Ascents',
    routes: 'Cumulative Routes',
    cleanRoutes: 'Cumulative Clean Routes',
    climbingDays: 'Cumulative Climbing Days',
    cumulativeGrade: climbingStyle === 'boulder' ? 'Cumulative Grade Performance (V-Scale)' : 'Cumulative Grade Performance (Ewbank)',
    cumulativeEffort: climbingStyle === 'boulder' ? 'Cumulative Grade Effort (V-Scale)' : 'Cumulative Grade Effort (Ewbank)'
  }[selectedStat] || 'Cumulative Clean Ascents');
  
  const minValue = useMemo(() => {
    const allValues = chartData.flatMap(data => 
      Object.entries(data)
        .filter(([key]) => key.startsWith('year'))
        .map(([_, value]) => value)
    ).filter(value => value !== null);
    
    return Math.floor(Math.min(...allValues));
  }, [chartData]);

  const dayOfYearTicks = useMemo(() => 
    Array.from({ length: 12 }, (_, i) => getDayOfYear(new Date(2000, i, 1))),
    []
  );

  const startYear = Math.min(...years);
  const getColorForYear = (year) => colors[(year - startYear) % colors.length];

  const CustomTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#666"
          fontSize={12}
        >
          {formatDayOfYear(payload.value)}
        </text>
      </g>
    );
  };

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 0, right: 30, left: 0, bottom: 20 }}
        >
          {/* Month background stripes */}
          {monthInfo.boundaries.map((startDay, index) => {
            const endDay = index < 11 ? monthInfo.boundaries[index + 1] : 366;
            return index % 2 === 0 ? (
              <ReferenceArea
                key={startDay}
                x1={startDay}
                x2={endDay}
                fill="#f3f4f6"
                fillOpacity={1}
              />
            ) : null;
          })}

          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e5e7eb"
            vertical={false}
          />

          <XAxis
            dataKey="dayOfYear"
            ticks={monthInfo.centers}
            tickSize={0}
            tickLine={false}
            tick={{ fontSize: 12, transform: 'translate(0, 6)' }}
            tickFormatter={formatDayOfYear}
            label={{ value: "Date", position: "bottom" }}
          />
          <YAxis
            label={{
              value: getYAxisLabel(),
              angle: -90,
              position: "middle",
              dx: -20
            }}
            domain={[minValue, 'auto']}
          />
          <Tooltip
            labelFormatter={(dayOfYear) => 
              `Day ${dayOfYear} (${formatDayOfYear(dayOfYear)})`
            }
            formatter={(value, name) => [value === null ? 'No ascents' : Number(value).toFixed(2), name]}
          />
          <Legend 
            layout="vertical"
            align="center"
            verticalAlign="middle"
            formatter={(value) => `${value} (${annualTotals[value]})`}
            wrapperStyle={{
              fontSize: '12px',
              padding: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              position: 'absolute',
              top: '5px',
              left: '65px',
              transform: 'translateY(0)',
              zIndex: 100
            }}
          />
          {years.map(year => (
            <Line
              key={year}
              type="stepAfter"
              dataKey={`year${year}`}
              name={year.toString()}
              stroke={getColorForYear(year)}
              dot={false}
              connectNulls
              animationDuration={300}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RoutesPerYearChart;